import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["frequency", "scheduleField", "action", "reportFields", "emailFields", "reviewFields", "steps", "stepButton", "panel", "previous", "next", "save", "name", "member", "memberError", "record", "assignee", "emails", "subject", "body", "status", "activationNote", "errors", "summaryName", "summarySchedule", "summaryAction", "summaryRecipients", "reviewName", "reviewSchedule", "reviewAction", "reviewRecipients", "reviewRecords", "reviewOptions", "reviewSubject", "reviewBody", "triggerKind", "resource", "eventFields", "eventType", "scheduleGroup", "conditionFields", "conditions", "condition", "conditionTemplate", "emptyConditions", "addCondition", "relativeDays", "relativeHours", "reminderFields", "thresholdFields", "resetField", "actionSelect", "actionChoices", "actionChoice", "resourceField", "triggerHelp", "conditionEmpty", "conditionHelp", "summaryZone", "summaryScope", "triggerControls", "messageAttribute", "insertAttribute", "messageAttributeHelp", "attributeError", "emailHelp", "attachmentSection", "automaticAttachment", "automaticAttachmentLabel", "recordPdfFields", "recordPdf", "recordPdfLabel", "noAttachment", "reviewAttachment", "attachmentRow", "extraReport", "reportThreshold", "reportThresholdFields", "updateDocumentFields", "deliveryHelp", "emailAddressFields", "teamDetails", "teamHeading", "teamSelection", "teamCount", "teamHelp", "memberEmpty", "reportHorizonFields", "documentFields", "documentType", "statementFields", "recipientSourceFields", "recipientSource", "creationFields", "createType", "createPricing", "createDate", "creationTypeFields", "deliveryOptions", "deliverEmail", "deliverNotify", "outputAttachmentFields", "attachOutput", "messageFields", "recipientFields", "genericFields"]
  static values = { timeZone: String, trigger: String, scope: String, messageAttributes: Array, recordPdf: Boolean, custom: Boolean, resources: Object, creationTypes: Array, outputFields: Object }

  connect() {
    this.creationType = this.deliveryOptionsTarget.getAttribute("initial_type") || ""
    this.step = 0
    this.stepsTarget.hidden = false
    this.activeMessageField = this.bodyTarget
    this.conditionIndex = this.hasConditionsTarget ? this.conditionTargets.length : 0
    this.actionSelectTarget.hidden = true
    this.actionChoicesTarget.hidden = false
    this.refresh()
    if (this.hasErrorsTarget) {
      const invalidStep = this.panelTargets.findIndex(panel => this.invalidField(panel))
      if (invalidStep >= 0) this.step = invalidStep
    }
    this.showStep()
    if (this.hasErrorsTarget) this.errorsTarget.focus()
  }

  refresh() {
    this.toggleFields(this.creationTypeFieldsTarget, this.actionTarget.value === "create_record")
    this.updateChoices(this.createTypeTarget, this.creationTypesValue.map(type => [type.label, type.key]), "Choose a record type")
    if (this.customValue) this.refreshCustom()
    const frequency = this.hasFrequencyTarget ? this.frequencyTarget.value : ""
    this.scheduleFieldTargets.forEach(element => this.toggleFields(element, this.isScheduled() && element.dataset.frequencies.split(" ").includes(frequency)))
    const action = this.actionTarget.value
    this.actionChoiceTargets.forEach(choice => { choice.checked = choice.value === action })
    const email = this.emailDelivery()
    this.subjectTarget.labels[0].textContent = action === "review" ? "Task title" : (email ? "Subject" : "Notification title")
    this.bodyTarget.labels[0].textContent = action === "review" ? "Task instructions" : "Message"
    this.refreshDelivery()
    this.refreshMessageAttributes()
    this.refreshAttachments()
    if (this.customValue) this.refreshCustomMembers()
    if (action === "review") this.selectOwner()
    if (this.hasActivationNoteTarget) this.activationNoteTarget.hidden = this.statusTarget.value !== "enabled"
    this.summarize()
  }

  isScheduled() {
    return this.customValue ? ["schedule", "watch", "date_offset"].includes(this.triggerKindTarget.value) : this.hasFrequencyTarget
  }

  chooseAction(event) {
    this.actionTarget.value = event.target.value
    this.refresh()
  }

  refreshCustom() {
    const action = this.actionTarget.value
    const available = [...new Set(Object.values(this.resourcesValue).flatMap(resource => resource.actions))]
    for (const option of this.actionTarget.options) option.disabled = !available.includes(option.value)
    const trigger = this.triggerKindTarget.value
    const compatible = Object.keys(this.resourcesValue).filter(key => this.resourcesValue[key].actions.includes(action) && this.resourcesValue[key].triggers.includes(trigger) && (action !== "create_record" || !this.creation() || this.creation().sources.includes(key)))
    const previous = this.resourceTarget.value
    for (const option of this.resourceTarget.options) {
      option.disabled = !compatible.includes(option.value) || (!option.value && trigger !== "schedule" && !(this.creationOption("report") && trigger === "watch"))
      option.hidden = option.disabled && !!option.value
    }
    const global = compatible.includes("") && (trigger === "schedule" || this.creationOption("report") && trigger === "watch")
    this.resourceTarget.options[0].textContent = global ? "Organization-wide (no records)" : "Choose a record type"
    if (!compatible.includes(previous)) this.resourceTarget.value = global ? "" : (compatible.length === 1 ? compatible[0] : "")
    if (previous !== this.resourceTarget.value) this.conditionsTarget.replaceChildren()
    this.resourceTarget.required = !global
    const resource = this.resourceTarget.value
    const events = this.resourcesValue[resource]?.events || []
    for (const option of this.eventTypeTarget.options) {
      option.disabled = !events.includes(option.value)
      option.hidden = option.disabled
    }
    if (this.eventTypeTarget.selectedOptions[0]?.disabled) this.eventTypeTarget.value = events[0] || "created"
    const help = {
      schedule: resource ? "Check the selected records on this schedule. Add conditions below to choose which records qualify." : "Run once for your organization each time this schedule occurs.",
      event: "Run after a record is created or updated. Only future matching events are included.",
      watch: "Check records on a schedule and act only when all conditions match.",
      date_offset: "Check a record's dates on a schedule, such as overdue invoices or upcoming expiry dates.",
      webhook: "Run when an external service sends a signed request. Connection details are available after saving."
    }
    this.triggerHelpTarget.textContent = help[trigger]
    this.toggleFields(this.scheduleGroupTarget, this.isScheduled())
    this.toggleFields(this.eventFieldsTarget, trigger === "event")
    this.toggleFields(this.conditionFieldsTarget, !!resource)
    this.refreshCustomConditions()
  }

  resourceConfig() {
    return this.resourcesValue[this.customValue ? this.resourceTarget.value : this.scopeValue] || {}
  }

  updateChoices(select, choices, blank = null) {
    const signature = JSON.stringify(choices)
    if (select.dataset.choices === signature) return
    const previous = select.value
    select.replaceChildren(...(blank ? [new Option(blank, "")] : []), ...choices.map(([label, key]) => new Option(label, key)))
    select.value = choices.some(([, key]) => key === previous) ? previous : (choices.length === 1 ? choices[0][1] : (blank ? "" : choices[0]?.[1] || ""))
    select.dataset.choices = signature
  }

  creation() { return this.actionTarget.value === "create_record" ? this.creationTypesValue.find(type => type.key === this.createTypeTarget.value) : null }
  creationOption(option) { return this.creation()?.options.includes(option) || false }
  emailDelivery() { return ["email", "record_email"].includes(this.actionTarget.value) || !!this.creation() && this.deliverEmailTarget.checked }
  teamDelivery() { return !["update_record", "webhook"].includes(this.actionTarget.value) && (this.actionTarget.value !== "create_record" || this.deliverNotifyTarget.checked) }

  refreshDelivery() {
    const action = this.actionTarget.value
    const config = this.resourceConfig()
    const creation = this.creation()
    if (creation && this.creationType !== creation.key) {
      this.deliverEmailTarget.checked = creation.defaults.email
      this.deliverNotifyTarget.checked = creation.defaults.notify
      this.attachOutputTarget.checked = creation.defaults.attach
      this.creationType = creation.key
    }
    this.updateChoices(this.documentTypeTarget, (config.documents || []).map(document => [document.label, document.key]), "Choose a document")
    this.updateChoices(this.createPricingTarget, config.creation_pricing || [])
    this.createDateTarget.querySelector("option[value='source']").textContent = config.creation_date ? `${config.creation_date} from matching record` : "Matching record's date"
    this.updateChoices(this.recipientSourceTarget, [["Email addresses", "addresses"], ...(config.recipient_sources || [])])
    this.deliveryActionChanged = this.deliveryAction !== `${action}:${creation?.key || ""}`
    this.deliveryAction = `${action}:${creation?.key || ""}`
    const email = this.emailDelivery()
    const team = this.teamDelivery()
    this.element.querySelector("#automation-delivery-heading").textContent = {
      create_record: "Generate and deliver", update_record: "Choose fields to update", webhook: "Configure the webhook request",
      review: "Assign the task", notify: "Set up the notification", channel_sync: "Set up channel updates"
    }[action] || "Set up delivery"
    const detailsOnly = ["update_record", "webhook"].includes(action)
    this.stepButtonTargets[2].lastElementChild.textContent = action === "create_record" ? "Details & delivery" : (detailsOnly ? "Details" : "Delivery")
    this.element.querySelector("button[data-step='2'][data-action='automation-form#goTo']:not([data-automation-form-target])").textContent = action === "create_record" ? "Edit details & delivery" : (detailsOnly ? "Edit details" : "Edit delivery")
    this.reportFieldsTargets.forEach(element => this.toggleFields(element, this.creationOption("report")))
    this.toggleFields(this.documentFieldsTarget, action === "record_email")
    this.toggleFields(this.statementFieldsTarget, this.creationOption("statement"))
    if (this.hasGenericFieldsTarget) this.toggleFields(this.genericFieldsTarget, ["update_record", "webhook"].includes(action))
    this.toggleFields(this.creationFieldsTarget, this.creationOption("commercial"))
    this.toggleFields(this.deliveryOptionsTarget, action === "create_record")
    this.toggleFields(this.outputAttachmentFieldsTarget, !!creation?.file && (email || team))
    this.toggleFields(this.recipientFieldsTarget, email || team)
    this.emailFieldsTargets.forEach(element => this.toggleFields(element, email))
    this.toggleFields(this.recipientSourceFieldsTarget, email)
    this.toggleFields(this.emailAddressFieldsTarget, email)
    this.toggleFields(this.teamDetailsTarget, team)
    this.reviewFieldsTargets.forEach(element => this.toggleFields(element, action === "review"))
    this.toggleFields(this.updateDocumentFieldsTarget, action === "record_email")
    this.toggleFields(this.messageFieldsTarget, email || team)
    this.emailsTarget.required = email && this.recipientSourceTarget.value === "addresses"
    this.emailsTarget.labels[0].textContent = this.emailsTarget.required ? "Email recipients" : "Additional email recipients (optional)"
    this.subjectTarget.labels[0].textContent = action === "review" ? "Task title" : (email ? "Subject" : "Notification title")
    this.toggleFields(this.reportThresholdFieldsTarget, this.creationOption("report") && this.reportThresholdTarget.checked)
    const report = this.element.querySelector("#rule_settings_report_name")
    const horizon = report.selectedOptions[0]?.dataset.horizon === "true" || this.extraReportTargets.some(extra => extra.checked && extra.dataset.horizon === "true")
    this.toggleFields(this.reportHorizonFieldsTarget, this.creationOption("report") && horizon)
    this.teamHeadingTarget.textContent = action === "notify" ? "Organization members" : "Team notifications"
    this.teamHelpTarget.textContent = action === "review" ? "The task owner is always included. Choose any other members who should receive the task notification." :
      (action === "create_record" ? "These members receive the created record and its attached file in the app." :
        (email ? "These members receive a notification in the app. They are separate from the email recipients." : "Choose at least one member to receive the notification in the app."))
    this.deliveryHelpTarget.textContent = {
      update_record: "Choose the fields to change. Values can come from the triggering record.", webhook: "Choose a connection and map the fields to send.",
      notify: "Choose who to notify and write their message.", review: "Assign an owner, set a due time, and write the task instructions.",
      email: "Choose email recipients and write your message.", record_email: "Choose a document, select its email recipients, and personalize the message.",
      channel_sync: "Choose who receives updates about the channel sync.", create_record: "Configure the new record, then choose whether to share its output."
    }[action]
  }

  resourceChanged() {
    this.conditionsTarget.replaceChildren()
    this.conditionIndex = 0
    this.refresh()
  }

  addCondition() {
    if (!this.resourceTarget.value || this.conditionTargets.length >= 10) return
    const html = this.conditionTemplateTarget.innerHTML.replaceAll("__INDEX__", String(this.conditionIndex++))
    this.conditionsTarget.insertAdjacentHTML("beforeend", html)
    this.refresh()
    this.conditionTargets.at(-1)?.querySelector("select")?.focus()
  }

  removeCondition(event) {
    event.currentTarget.closest(".automation-condition").remove()
    this.refresh()
  }

  refreshCustomConditions() {
    const fields = this.resourcesValue[this.resourceTarget.value]?.fields || []
    const kinds = []
    const settings = []
    this.conditionTargets.forEach(row => {
      const field = row.querySelector("[data-condition-field]")
      const selected = field.value
      // Keep the active select in place while the user changes its value.
      if (field.dataset.resource !== this.resourceTarget.value) {
        field.replaceChildren(new Option("Choose a field", ""), ...fields.map(([label, value]) => new Option(label, value)))
        field.value = selected
        field.dataset.resource = this.resourceTarget.value
      }
      const metadata = fields.find(([, value]) => value === field.value)
      if (metadata?.[4]) settings.push(metadata[4])
      const type = metadata?.[2]
      const date = ["date", "datetime", "timestamp"].includes(type)
      const operator = row.querySelector("[data-condition-operator]")
      const business = field.value.startsWith("business.")
      const eventOperators = ["changed_to", "changed_to_any", "became_positive"]
      for (const option of operator.options) {
        const unavailable = business ? !["eq", "not_eq"].includes(option.value) :
          (eventOperators.includes(option.value) && (this.triggerKindTarget.value !== "event" || (option.value === "became_positive" && !["integer", "decimal", "float", "big_integer"].includes(type))))
        option.disabled = unavailable
        option.hidden = unavailable
      }
      if (operator.selectedOptions[0]?.disabled) {
        const value = row.querySelector("[data-condition-value]")
        if (operator.value === "became_positive") value.value = "0"
        operator.value = { changed_to: "eq", changed_to_any: "in", became_positive: "gt" }[operator.value] || "eq"
      }
      const labels = date ? { lt: "Before", lte: "On or before", gt: "After", gte: "On or after" } : { lt: "Less than", lte: "At most", gt: "Greater than", gte: "At least" }
      for (const option of operator.options) if (labels[option.value]) option.textContent = labels[option.value]
      const unary = ["present", "blank", "became_positive"].includes(operator.value)
      const kind = row.querySelector("[data-condition-kind]")
      if (!date || eventOperators.includes(operator.value) || ["in", "not_in"].includes(operator.value)) kind.value = "literal"
      this.toggleFields(row.querySelector("[data-condition-value-fields]"), !unary && date)
      // Literal is the server default when a non-date comparison has no kind field.
      this.toggleFields(row.querySelector("[data-condition-literal]"), !unary && kind.value === "literal")
      row.querySelector("[data-condition-literal]").classList.toggle("mt-2", date)
      this.updateConditionValue(row, type, metadata?.[3], ["in", "not_in", "changed_to_any"].includes(operator.value))
      if (!unary) kinds.push(kind.value)
    })
    this.conditionEmptyTarget.hidden = this.conditionTargets.length > 0
    const required = ["watch", "date_offset"].includes(this.triggerKindTarget.value)
    this.conditionHelpTarget.textContent = required ? "Add at least one condition to define what should trigger the action." : "All records are included. Add a condition to narrow them down."
    this.emptyConditionsTarget.disabled = this.conditionTargets.length > 0
    this.addConditionTarget.disabled = this.conditionTargets.length >= 10
    this.toggleFields(this.relativeDaysTarget, settings.includes("days") || kinds.some(kind => ["days_ago", "days_from_now"].includes(kind)))
    this.toggleFields(this.relativeHoursTarget, kinds.includes("hours_ago"))
    this.toggleFields(this.thresholdFieldsTarget, settings.includes("threshold"))
    this.toggleFields(this.reminderFieldsTarget, !!this.resourceTarget.value && this.isScheduled())
    if (this.resetFieldTarget.dataset.resource !== this.resourceTarget.value) {
      const selected = this.resetFieldTarget.value
      this.resetFieldTarget.replaceChildren(new Option("Never reset", ""), ...fields.filter(([, field]) => !field.startsWith("business.")).map(([label, field]) => new Option(label, field)))
      this.resetFieldTarget.value = selected
      this.resetFieldTarget.dataset.resource = this.resourceTarget.value
    }
  }

  updateConditionValue(row, type, enumValues, list) {
    let field = row.querySelector("[data-condition-value]")
    const choices = list ? null : (type === "boolean" ? [["Yes", "true"], ["No", "false"]] : enumValues?.map(value => [value.replaceAll("_", " ").replace(/^./, letter => letter.toUpperCase()), value]))
    const signature = JSON.stringify([type, choices, list])
    if (field.dataset.signature === signature) return
    const value = field.value
    const replacement = document.createElement(choices ? "select" : "input")
    for (const attribute of field.attributes) replacement.setAttribute(attribute.name, attribute.value)
    replacement.dataset.signature = signature
    replacement.maxLength = list ? 2000 : 500
    if (choices) {
      replacement.removeAttribute("type")
      replacement.replaceChildren(new Option("Choose a value", ""), ...choices.map(([label, key]) => new Option(label, key)))
    } else {
      replacement.type = list ? "text" : (["integer", "decimal", "float", "big_integer"].includes(type) ? "number" : (type === "date" ? "date" : "text"))
      replacement.step = ["integer", "big_integer"].includes(type) ? "1" : "any"
      replacement.placeholder = list ? (enumValues?.join(", ") || "Separate values with commas") : (["datetime", "timestamp"].includes(type) ? "YYYY-MM-DDTHH:MM:SS+00:00" : "Enter a value")
    }
    replacement.value = value
    field.replaceWith(replacement)
  }

  refreshMessageAttributes() {
    let groups = this.customValue ? this.resourcesValue[this.resourceTarget.value]?.message_attributes || [] : this.messageAttributesValue
    if (this.creation()) groups = [...groups, this.outputFieldsValue]
    const selected = this.messageAttributeTarget.value
    const options = [new Option("Choose a message field", "")]
    groups.forEach(group => {
      const optgroup = document.createElement("optgroup")
      optgroup.label = group.label
      optgroup.append(...group.fields.map(field => new Option(field.label, field.value)))
      options.push(optgroup)
    })
    const signature = JSON.stringify(groups)
    if (signature !== this.messageAttributeSignature) {
      this.messageAttributeTarget.replaceChildren(...options)
      this.messageAttributeTarget.value = selected
      this.messageAttributeSignature = signature
    }
    this.insertAttributeTarget.disabled = !this.messageAttributeTarget.value
    this.availableMessageFields = new Set(["record_label", ...groups.flatMap(group => group.fields.map(field => field.value))])
    this.messageAttributeHelpTarget.textContent = "Insert at the cursor. Values are filled in when the automation runs."
  }

  validateMessageAttributes() {
    if (!this.availableMessageFields) return
    const unavailable = new Set()
    for (const field of [this.subjectTarget, this.bodyTarget]) {
      const invalid = []
      if (field.disabled) { field.setCustomValidity(""); continue }
      const expressions = field.value.match(/{{[\s\S]*?}}|{%[\s\S]*?%}/g) || []
      for (const expression of expressions) {
        const references = expression.replace(/"(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'/g, "")
        for (const token of references.match(/\b(?:record|output)\.[a-zA-Z_]\w*|\brecord_label\b/g) || []) {
          if (!this.availableMessageFields.has(token)) invalid.push(token)
        }
      }
      invalid.forEach(token => unavailable.add(token))
      field.setCustomValidity(invalid.length ? `These fields aren't available for the selected record type: ${[...new Set(invalid)].join(", ")}. Choose replacement fields below.` : "")
    }
    this.attributeErrorTarget.hidden = unavailable.size === 0
    this.attributeErrorTarget.textContent = unavailable.size ? `Update these fields for the selected record type: ${[...unavailable].join(", ")}.` : ""
  }

  refreshAttachments() {
    const action = this.actionTarget.value
    const config = this.resourceConfig()
    const creation = this.creation()
    const supportsPdf = config.record_pdf
    const generated = !!creation?.file && this.attachOutputTarget.checked && (this.emailDelivery() || this.teamDelivery())
    const automatic = action === "record_email" || generated
    this.attachmentSectionTarget.hidden = !automatic && !(action === "email" && supportsPdf)
    this.toggleFields(this.recordPdfFieldsTarget, action === "email" && supportsPdf)
    if (action !== "email" || !supportsPdf) this.recordPdfTarget.checked = false
    const name = this.customValue ? this.resourceTarget.selectedOptions[0]?.textContent : this.scopeValue
    this.recordPdfLabelTarget.textContent = `Attach ${name?.toLowerCase() || "record"} PDF`
    const format = this.element.querySelector("#rule_settings_format")?.value.toUpperCase() || "PDF"
    this.attachmentLabel = generated ? (this.creationOption("report") ? `Generated report file (${format})` : `${creation.label} PDF`) :
      (action === "record_email" ? this.documentTypeTarget.selectedOptions[0]?.textContent || "Choose a document" :
        (action === "email" && this.recordPdfTarget.checked ? `${name} PDF` : "None"))
    this.automaticAttachmentTarget.hidden = !automatic
    this.automaticAttachmentLabelTarget.textContent = this.attachmentLabel
    this.attachmentRowTargets.forEach(element => { element.hidden = !this.emailDelivery() && !generated })
    this.reviewAttachmentTarget.textContent = this.attachmentLabel
    this.emailHelpTarget.textContent = this.recipientSourceTarget.value !== "addresses" ? "Optional. These addresses also receive a copy. Separate addresses with commas." : "Separate addresses with commas."
  }

  selectedOutputReadPermissions() {
    if (this.creation()) return this.creation().permissions
    const config = this.resourceConfig()
    if (this.actionTarget.value === "record_email") return (config.documents?.find(document => document.key === this.documentTypeTarget.value)?.permissions || []).filter(permission => permission.endsWith(".read"))
    return []
  }

  refreshCustomMembers() {
    const resource = this.resourceTarget.value
    const reports = [this.element.querySelector("#rule_settings_report_name")?.value, ...this.extraReportTargets.filter(report => report.checked).map(report => report.value)]
    const checks = this.conditionTargets.map(row => row.querySelector("[data-condition-field]").value).filter(field => field.startsWith("business."))
    this.memberTargets.forEach(member => {
      const allowed = this.teamDelivery() && (!resource || JSON.parse(member.dataset.resources).includes(resource)) &&
        checks.every(field => JSON.parse(member.dataset.businessConditions).includes(field)) &&
        (!this.creationOption("report") || reports.every(report => JSON.parse(member.dataset.reports).includes(report))) &&
        this.selectedOutputReadPermissions().every(permission => JSON.parse(member.dataset.permissions).includes(permission))
      member.disabled = !allowed
      member.closest("label").hidden = !allowed
      if (!allowed && this.teamDelivery()) member.checked = false
    })
    this.memberEmptyTarget.hidden = this.memberTargets.some(member => !member.disabled)
    if (this.hasAssigneeTarget) {
      for (const option of this.assigneeTarget.options) option.disabled = !this.memberTargets.some(member => member.value === option.value && !member.disabled)
      if (this.assigneeTarget.selectedOptions[0]?.disabled) this.assigneeTarget.value = [...this.assigneeTarget.options].find(option => !option.disabled)?.value || ""
    }
  }

  toggleFields(element, visible) {
    element.hidden = !visible
    element.querySelectorAll("input, select, textarea").forEach(field => { field.disabled = !visible })
  }

  selectOwner() {
    if (!this.hasAssigneeTarget || this.actionTarget.value !== "review") return
    const owner = this.memberTargets.find(member => member.value === this.assigneeTarget.value)
    if (owner) owner.checked = true
    this.summarize()
  }

  summarize() {
    this.validateMessageAttributes()
    const name = this.nameTarget.value.trim() || "Untitled automation"
    const action = this.creation() ? `Create a record · ${this.creation().label}` : this.actionTarget.selectedOptions[0]?.textContent || ""
    const members = this.memberTargets.filter(member => member.checked && !member.disabled).map(member => member.dataset.label)
    this.teamSelectionTarget.textContent = members.join(", ") || "Choose members"
    this.teamCountTarget.textContent = members.length
    if (this.deliveryActionChanged || !members.length) {
      this.teamDetailsTarget.open = this.actionTarget.value === "notify" || !members.length
      this.deliveryActionChanged = false
    }
    const parts = this.teamDelivery() ? [members.join(", ") || "Choose organization members"] : []
    if (this.emailDelivery() && this.emailsTarget.value.trim()) parts.push(`Email: ${this.emailsTarget.value.trim()}`)
    if (this.emailDelivery() && this.recipientSourceTarget.value !== "addresses") parts.push(this.recipientSourceTarget.selectedOptions[0]?.textContent || "Choose recipients")
    let recipients = parts.join(" · ") || (this.creation() && !this.emailDelivery() ? "Save only · No delivery" : (["update_record", "webhook"].includes(this.actionTarget.value) ? "No message delivery" : "Choose email recipients"))
    if (this.actionTarget.value === "review" && this.hasAssigneeTarget) recipients += ` · Owner: ${this.assigneeTarget.selectedOptions[0]?.textContent || "Choose an owner"}`
    const schedule = this.scheduleSummary()
    this.summaryNameTarget.textContent = name
    this.summaryScheduleTarget.textContent = schedule
    const nodes = JSON.parse(this.element.querySelector("input[name='rule[workflow_json]']")?.value || "[]")
    const countSteps = steps => steps.reduce((count, step) => count + 1 + countSteps(step.then || []) + countSteps(step.else || []), 0)
    const extraSteps = Math.max(0, countSteps(nodes) - 1)
    this.summaryActionTarget.textContent = extraSteps ? `${action} · ${extraSteps} additional steps` : action
    this.summaryRecipientsTarget.textContent = recipients
    this.summaryZoneTarget.hidden = !this.isScheduled()
    this.summaryScopeTarget.textContent = this.customValue ? this.customConditionsSummary() : this.scopeValue
    this.reviewNameTarget.textContent = name
    this.reviewScheduleTarget.textContent = this.isScheduled() ? `${schedule} (${this.timeZoneValue})` : schedule
    this.reviewActionTarget.textContent = action
    this.reviewRecipientsTarget.textContent = recipients
    const records = this.recordTargets.filter(record => record.checked)
    this.reviewRecordsTarget.textContent = this.customValue ? this.customConditionsSummary() : (records.length ? `${records.length} selected; template conditions still apply` : "All eligible records")
    const options = []
    const optionIds = ["rule_settings_days", "rule_settings_hours", "rule_settings_threshold", "rule_settings_cooldown_days"]
    if (this.actionTarget.value === "review") optionIds.push("rule_settings_task_due_hours")
    if (this.creationOption("report")) optionIds.push("rule_settings_report_name", "rule_settings_format", "rule_settings_report_period", "rule_settings_report_threshold", "rule_settings_report_days")
    if (this.actionTarget.value === "record_email") optionIds.push("rule_settings_document_type")
    if (this.creationOption("statement")) optionIds.push("rule_settings_statement_kind", "rule_settings_statement_period")
    if (this.actionTarget.value === "create_record") optionIds.push("rule_settings_create_type", "rule_settings_create_date", "rule_settings_create_date_offset", "rule_settings_create_due_days", "rule_settings_create_pricing")
    optionIds.forEach(id => {
      const field = this.element.querySelector(`#${id}`)
      if (field && !field.disabled) options.push(`${field.labels[0]?.textContent}: ${field.selectedOptions ? field.selectedOptions[0]?.textContent : field.value}`)
    })
    if (this.creationOption("report")) this.extraReportTargets.filter(report => report.checked).forEach(report => options.push(`Additional report: ${report.closest("label").textContent.trim()}`))
    this.reviewOptionsTarget.textContent = options.join(" · ") || (this.customValue ? "No additional options" : "Template defaults")
    this.reviewSubjectTarget.textContent = this.subjectTarget.value
    this.reviewBodyTarget.textContent = this.bodyTarget.value
    this.reviewSubjectTarget.closest(".automation-message").hidden = this.subjectTarget.disabled
    if (members.length) this.memberErrorTarget.hidden = true
  }

  scheduleSummary() {
    if (this.customValue && !this.isScheduled()) {
      const event = this.element.querySelector("#rule_settings_event_type")
      return this.triggerKindTarget.value === "event" ? event.selectedOptions[0].textContent : this.triggerKindTarget.selectedOptions[0].textContent
    }
    if (!this.hasFrequencyTarget) return this.triggerValue
    const value = id => this.element.querySelector(`#${id}`)?.value
    const time = value("rule_schedule_time") || "Choose a time"
    switch (this.frequencyTarget.value) {
      case "once": return `Once · ${value("rule_schedule_run_at")?.replace("T", " ") || "Choose a date and time"}`
      case "daily": return `Every day at ${time}`
      case "weekdays": return `Monday–Friday at ${time}`
      case "weekly": return `Every ${this.element.querySelector("#rule_schedule_weekday")?.selectedOptions[0]?.textContent} at ${time}`
      case "monthly": {
        const weekend = this.element.querySelector("#rule_schedule_business_day")?.checked ? " · Weekends move to Monday" : ""
        return `Day ${value("rule_schedule_day")} of each month at ${time}${weekend}`
      }
      case "interval": return `Every ${value("rule_schedule_interval_minutes")} minutes`
      default: return this.triggerValue
    }
  }

  customConditionsSummary() {
    if (!this.resourceTarget.value) return "Your organization"
    const name = this.resourceTarget.selectedOptions[0].textContent
    const conditions = this.conditionTargets.map(row => {
      const text = selector => row.querySelector(selector)?.selectedOptions?.[0]?.textContent || ""
      const operator = row.querySelector("[data-condition-operator]").value
      const value = ["present", "blank", "became_positive"].includes(operator) ? "" : (row.querySelector("[data-condition-kind]").value === "literal" ? (row.querySelector("[data-condition-value]").selectedOptions?.[0]?.textContent || row.querySelector("[data-condition-value]").value) : text("[data-condition-kind]"))
      return `${text("[data-condition-field]")} ${text("[data-condition-operator]")} ${value}`.trim()
    })
    const groups = JSON.parse(this.element.querySelector("input[name='rule[condition_groups_json]']")?.value || "[]")
    if (groups.length) return `${name} · ${conditions.length} conditions · ${groups.length} groups`
    return `${name} · ${conditions.length ? conditions.join(this.element.querySelector("#rule_condition_mode")?.value === "any" ? " OR " : " AND ") : "All records"}`
  }

  next() {
    if (this.validateStep(this.step)) { this.step += 1; this.showStep(true) }
  }

  previous() {
    this.step -= 1
    this.showStep(true)
  }

  goTo(event) {
    const destination = Number(event.currentTarget.dataset.step)
    for (let index = this.step; index < destination; index++) {
      if (!this.validateStep(index)) return
    }
    this.step = destination
    this.showStep(true)
  }

  showStep(focus = false) {
    this.panelTargets.forEach((panel, index) => { panel.hidden = index !== this.step })
    this.stepButtonTargets.forEach((button, index) => {
      if (index === this.step) button.setAttribute("aria-current", "step")
      else button.removeAttribute("aria-current")
      button.classList.toggle("is-complete", index < this.step)
    })
    this.previousTarget.hidden = this.step === 0
    this.nextTarget.hidden = this.step === this.panelTargets.length - 1
    this.saveTarget.hidden = this.step !== this.panelTargets.length - 1
    if (focus) {
      const heading = [...this.panelTargets[this.step].querySelectorAll("h2")].find(heading => !heading.closest("[hidden]"))
      heading.setAttribute("tabindex", "-1")
      heading.focus({ preventScroll: true })
      this.stepsTarget.scrollIntoView({ block: "start", behavior: "instant" })
    }
  }

  validateStep(index) {
    const panel = this.panelTargets[index]
    const invalid = this.invalidField(panel)
    if (invalid) {
      this.step = index
      this.showStep()
      const details = invalid.closest("details")
      if (details) details.open = true
      invalid.reportValidity()
      invalid.focus()
      return false
    }
    if (panel.dataset.stepKind === "trigger" && this.customValue && this.resourceTarget.value && ["watch", "date_offset"].includes(this.triggerKindTarget.value) && !this.conditionTargets.length) {
      this.step = index
      this.showStep()
      this.addCondition()
      this.conditionTargets[0]?.querySelector("select")?.reportValidity()
      return false
    }
    if (panel.dataset.stepKind === "trigger" && this.recordTargets.some(record => record.dataset.recordRequired === "true") && !this.recordTargets.some(record => record.checked)) {
      this.step = index
      this.showStep()
      const record = this.recordTargets[0]
      // A filtered-out choice must be revealed before focusing a required selection.
      if (record) { record.closest("label").hidden = false; record.setCustomValidity("Choose at least one record for this dated reminder."); record.reportValidity(); record.setCustomValidity("") }
      return false
    }
    if (panel.dataset.stepKind === "delivery" && this.teamDelivery() && !this.memberTargets.some(member => member.checked && !member.disabled)) {
      this.step = index
      this.showStep()
      this.teamDetailsTarget.open = true
      this.memberErrorTarget.hidden = false
      const member = this.memberTargets.find(member => !member.disabled)
      this.memberErrorTarget.textContent = member ? "Choose at least one organization member." : "No eligible members have access to this setup. Choose a different record type or update member permissions."
      if (member) member.closest("label").hidden = false
      member?.focus()
      this.memberErrorTarget.scrollIntoView({ block: "center" })
      return false
    }
    if (panel.dataset.stepKind === "delivery" && ["update_record", "webhook"].includes(this.actionTarget.value)) {
      const element = this.genericFieldsTarget.querySelector("[data-controller='automation-workflow']")
      const controller = this.application.getControllerForElementAndIdentifier(element, "automation-workflow")
      if (controller && !controller.validateFields()) {
        this.step = index
        this.showStep()
        return false
      }
    }
    return true
  }

  invalidField(panel) {
    return [...panel.querySelectorAll("input, select, textarea")].find(field => !field.disabled && !field.closest("[hidden]:not([data-automation-form-target='panel'])") && !field.checkValidity())
  }

  submit(event) {
    // Enter inside a field follows the same steps as the Continue button.
    if (this.step < this.panelTargets.length - 1) {
      event.preventDefault()
      this.next()
      return
    }
    for (let index = 0; index < this.panelTargets.length; index++) {
      if (!this.validateStep(index)) { event.preventDefault(); return }
    }
  }

  filterChoices(event) {
    const query = event.target.value.toLocaleLowerCase().trim()
    const group = this.element.querySelector(`.automation-choice-list[data-choice-group="${event.target.dataset.choiceGroup}"]`)
    group.querySelectorAll("label").forEach(label => { label.hidden = !label.textContent.toLocaleLowerCase().includes(query) })
    const empty = group.querySelector("[data-choice-empty]")
    if (empty) empty.hidden = [...group.querySelectorAll("label")].some(label => !label.hidden) || !query
  }

  rememberField(event) { this.activeMessageField = event.target }

  insertVariable(event) {
    const field = this.activeMessageField || this.bodyTarget
    const variable = event.currentTarget.dataset.variable || this.messageAttributeTarget.value
    if (!variable) return
    const token = `{{ ${variable} }}`
    field.setRangeText(token, field.selectionStart, field.selectionEnd, "end")
    field.focus()
    this.summarize()
  }
}
